import {ColorOverlayFilter, OutlineFilter} from 'pixi-filters';
import {Filter} from 'pixi.js';
import {IPoint} from '../interfaces/primitives/IPoint';
import {Card} from '../pixi_scene/game_scene/game_table/table_objects/stacks/Card';
import {ActiveData} from '../data/ActiveData';
import {ArrayWrap} from '../data/active_data/base/static_config_data/ArrayWrap';
import {CardData, ICardData} from '../data/active_data/field_config/card_stack_data/CardData';
import {DynamicData} from '../data/DynamicData';
import {StaticData} from '../data/StaticData';

export class CardSelector
{
    static selectedCards: Card[] = [];
    static dragIndexByTempCard: Map<Card, number> = new Map<Card, number>();
    static dragIndexByTempCardNormalized: Map<Card, number> = new Map<Card, number>();
    private static highlightFilter: ColorOverlayFilter = new ColorOverlayFilter(0x0, .2);
    private static selectFilter = new OutlineFilter(4, 0xff0000);
    private static hightlightedCard: Card | null;

    /**
     * Подсветить колоду
     * @param card - колода которую нужно подсветить
     */
    static highlight(card: Card): void {
        CardSelector.unhighlight();
        CardSelector.tryAddFilter(card, CardSelector.highlightFilter, true);
        CardSelector.hightlightedCard = card;
    }

    /**
     * Обновить цвет обводки колод при выделении текущим игроком
     */
    static updateSelectFilterColor(): void {
        let color: string = StaticData.isAdmin ? '777777' : DynamicData.playersColorsMap.get(StaticData.playerId)!.split('#')[1];
        CardSelector.selectFilter.color = parseInt('0x' + color);
    }

    /**
     * Убрать подсветку с текущей подсвеченной колоды
     */
    static unhighlight(): void {
        CardSelector.hightlightedCard && CardSelector.tryRemoveFilter(CardSelector.hightlightedCard, CardSelector.highlightFilter);
        CardSelector.hightlightedCard = null;
    }

    /**
     * Убрать подсветку с текущей подсвеченной колоды
     */
    static highlighted(): Card | null {
        return CardSelector.hightlightedCard;
    }

    /**
     * Выбрать указанную колоду
     * @param card - колода которую нужно выбрать
     * @param currentOnly - выбрать только ёё, если false - добавляет колоду к выбранным
     */
    static selectCard(card: Card, currentOnly: boolean = true): void {
        currentOnly && CardSelector.unselectAll();
        if (!CardSelector.selected(card)) {
            CardSelector.selectedCards.push(card);
            CardSelector.tryAddFilter(card, CardSelector.selectFilter);
        }
    }

    /**
     * Снять выбор со всех выбранных колод
     */
    static unselectAll(sitDown: boolean = true): void {
        let card: Card;
        while (CardSelector.selectedCards.length) {
            card = CardSelector.selectedCards.shift()!;
            CardSelector.tryRemoveFilter(card, CardSelector.selectFilter);
            sitDown && card.sitDown();
        }
    }

    /**
     * Проверить, является ли данная колода одной из выбранных
     * @param card - колода которую нужно проверить
     */
    static selected(card: Card): boolean {
        return CardSelector.selectedCards.indexOf(card) > -1;
    }

    /**
     * Переместить выделенные колоды на указанную дельту по вертикали и горизонтали
     * @param moveDelta объект, содеращий дельты перемещений по осям
     */
    static moveSelectedOnBy(moveDelta: IPoint) {
        for (let card of CardSelector.selectedCards) {
            card.x += moveDelta.x;
            card.y += moveDelta.y;
        }
    }

    /**
     * Убрать выделение с указанной колоды
     * @param card - колода, которую нужно удалить
     */
    static unselectCard(card: Card) {
        if (!CardSelector.selected(card)) {
            return;
        }
        CardSelector.selectedCards.splice(CardSelector.selectedCards.indexOf(card), 1);
        CardSelector.tryRemoveFilter(card, CardSelector.selectFilter);
    }

    /**
     * Рассчет вспомогательных данных для таскаемых в руке карт
     * таскаемые карты - O, главная таскаемая карта - X
     * главная таскаемая карта всегда имеет значение 0 во временных данных
     *
     * --O---X-OO-
     *
     * dragIndexByTempCard
     * [-4, 0, 2, 3]
     *
     * dragIndexByTempCardNormalized
     * [-1, 0, 1, 2]
     *
     * @param tempCards - массив таскаемых над рукой карт
     */
    static setTempCardsDragIndexes(tempCards: Card[]) {
        this.dragIndexByTempCard.clear();
        this.dragIndexByTempCardNormalized.clear();
        let mainDraggingCard: Card = tempCards.find(card => card.mainDragging)!;
        let currentPlayerCards: ArrayWrap<CardData, ICardData> = ActiveData.root.getCurrentPlayerData().cards;
        let mainDraggingCardIndex: number = currentPlayerCards.indexOf(mainDraggingCard.activeData);
        tempCards.forEach(card => {
            this.dragIndexByTempCard.set(card, currentPlayerCards.indexOf(card.activeData) - mainDraggingCardIndex);
        });
        tempCards.sort((card1: Card, card2: Card) => {
            return this.dragIndexByTempCard.get(card1)! - this.dragIndexByTempCard.get(card2)!;
        });
        let minIndex: number = tempCards.reduce((minIndex: number, card: Card) => Math.min(minIndex, this.dragIndexByTempCard.get(card)!), 0);
        let maxIndex: number = tempCards.reduce((maxIndex: number, card: Card) => Math.max(maxIndex, this.dragIndexByTempCard.get(card)!), 0);
        let card: Card | undefined;
        let highestIndex: number = 0;
        let smallestIndex: number = 0;
        let tempCards2: Card[] = [mainDraggingCard];
        this.dragIndexByTempCardNormalized.set(mainDraggingCard, 0);
        for (let index: number = 0; index <= maxIndex; index++) {
            card = tempCards.find(card => this.dragIndexByTempCard.get(card) == index);
            if (card && tempCards2.indexOf(card) == -1) {
                tempCards2.push(card);
                this.dragIndexByTempCardNormalized.set(card, ++highestIndex);
            }
        }
        for (let index: number = 0; index >= minIndex; index--) {
            card = tempCards.find(card => this.dragIndexByTempCard.get(card) == index);
            if (card && tempCards2.indexOf(card) == -1) {
                this.dragIndexByTempCardNormalized.set(card, --smallestIndex);
                tempCards2.unshift(card);
            }
        }
    }

    /**
     * Получить карты для которых сработает горячая клавиша
     */
    static getHotkeyCards(): Card[] {
        let returning: Card[] = [];
        if (CardSelector.selectedCards.length) {
            returning = this.selectedCards;
        } else if (CardSelector.hightlightedCard && CardSelector.hightlightedCard.activeData.id != -1) {
            returning = [CardSelector.hightlightedCard];
        }
        return returning;
    }

    /**
     * Применяем фильтр к колоде
     * @param card - колода к которой применяем фильтр
     * @param filter - применяемый к данной колоде фильтр
     * @param toEnd - флаг указывающий на очередность фильтра при добавлении, елси true - фильтр добавтся в конец массива фильтров объекта
     */
    private static tryAddFilter(card: Card, filter: Filter, toEnd: boolean = false): void {
        if (card.filters?.length) {
            card.filters.indexOf(filter) == -1 && (toEnd ? card.filters.push(filter) : card.filters.unshift(filter));
        } else {
            card.filters = [filter];
        }
    }

    /**
     * Убираем фильтр с колоды
     * @param card - колода с которой уюираем фильтр
     * @param filter - убираемый с данной колоды фильтр
     */
    private static tryRemoveFilter(card: Card, filter: Filter): void {
        if (card.filters?.length) {
            let index: number = card.filters.indexOf(filter);
            index > -1 && card.filters.splice(index, 1);
        }
    }
}
